/**
 * emailContextState — the closed state set behind the pinned pane's per-email
 * context strip, its BINDING copy, and the single action each state offers.
 *
 * Shared by ora and the Outlook add-in (`@product-app/common/workflows/…`) so
 * one copy table has one consumer per host and neither can drift.
 *
 * Why a NEW module rather than an entry in `statusLabels.ts`: that file's
 * vocabulary is substrate-STATUS shaped ("paused_hitl" → "Waiting on you").
 * These are TRIAGE states for an EMAIL, resolved server-side over the runs on
 * it plus the analysis row — a different key space with a different lifetime.
 *
 * The module is PURE: no fetch, no Office, no React. That is what lets a probe
 * render it over a captured server response.
 *
 * Consumers of this closed state set and its copy table: `hitl-notifications`
 * (which links the AM here from its notification, and must not invent a
 * seventh state or a different pill) and `shared-decision-components` (which
 * renders `WaitingQueue` / `EmailContextStrip` at width in ora — the SAME
 * sources, never a fork). The full table, and the queue-membership rule this
 * module's states are populated FROM, are documented for both at
 * `../../../apps/outlook-addin/docs/pinned-pane.md`
 * (`ui/apps/outlook-addin/docs/pinned-pane.md`), kept in sync with this
 * file by `src/docs/pinnedPaneDocSync.test.ts` rather than retyped.
 *
 * Queue membership (which statuses even reach `EmailRunItem`/the pane's
 * queue) is a SEPARATE rule from this state set, and lives at
 * `WAITING_STATUSES` in `repos/workflows/src/workflows/tools/waiting_queue.py`
 * — that is the ONE place a status is added to or removed from what the pane
 * lists. This module has no opinion on it.
 */

import { formatDate } from "./formatTime";
import type { EmailRunItem, EmailRunStateResponse } from "./types";
import { NEEDS_YOU_LABEL, STATUS_LABELS, statusDisplayLabel } from "./statusLabels";
import { warningMarker } from "./warningAcknowledgement";

/**
 * The closed set. Eight come from the server (`EmailRunStateResponse.state`);
 * `resolving` and `no_item` are CLIENT states the server never sends — the
 * lookup is in flight, or there is nothing selected to look up.
 */
export type EmailContextState =
  | "resolving"
  | "not_analyzed"
  | "brief_ready"
  | "no_action"
  | "unmatched"
  | "running"
  | "input_required"
  | "completed"
  | "several_runs"
  | "no_item";

/** The eight states the SERVER may return, asserted before any is trusted. */
export const SERVER_EMAIL_CONTEXT_STATES: readonly EmailContextState[] = [
  "not_analyzed",
  "brief_ready",
  "no_action",
  "unmatched",
  "running",
  "input_required",
  "completed",
  "several_runs",
] as const;

/**
 * BINDING copy — the EMUW Drawers design plus the operator's 2026-08-25
 * amendments (PRD AC-4) and the 2026-08-28 current-email restyle:
 * "Review & Approve" replaces "Open the brief".
 *
 * Three rows deliberately depart from the design and the departures are
 * recorded in the PRD: "Review required" replaces "Brief ready"; "Input
 * required" and "Completed on <date>" are new.
 *
 * `several_runs` is absent by construction — its pill is computed from two
 * counts by `severalRunsPill`, so there is no static string to get wrong.
 */
export const CONTEXT_PILL: Record<Exclude<EmailContextState, "several_runs">, string> = {
  resolving: "Checking…",
  not_analyzed: "Not triaged",
  brief_ready: "Review required",
  running: "Running",
  input_required: NEEDS_YOU_LABEL,
  completed: "Completed on {date}",
  no_action: "Triaged - no action needed",
  unmatched: "Triaged - policy not identified",
  no_item: "Nothing for this item",
};

/**
 * A finished email's pill (R53/R55, 2026-10-06): "<label> on <date>", where
 * <label> is the NEWEST finished run's own status label — "Completed", "Error",
 * "Cancelled", "Timed Out", "Rejected" — plus "*" while any finished run on the
 * email has a warning line nobody has acknowledged. `CONTEXT_PILL.completed` is
 * this template with "Completed", so a clean completed email reads as before.
 */
export const FINISHED_ON_PILL = "{label} on {date}";

/** BINDING copy — one primary action per state, exactly the AC-4 table. */
export const CONTEXT_PRIMARY_ACTION = {
  not_analyzed: "Run analysis",
  brief_ready: "Review & Approve",
  // Story 26741 AC-5. An unmatched email HAS a brief; what it lacks is the
  // policy, so the action names the gap rather than promising a review of
  // something already decided.
  unmatched: "Add the missing details",
  running: "Open the run",
  input_required: "Open the decision",
  completed: "Relaunch",
  several_runs: "Open the one that needs you",
} as const;

/**
 * Story 26741 AC-4. `no_action` is the ONE server state with no primary
 * action: ora triaged the mail and deliberately did nothing, so offering a
 * launch button as the obvious next step invites the AM to redo work that was
 * skipped on purpose.
 *
 * It is NOT a dead end. The override below is offered instead — deliberately
 * as the secondary, because the whole point is that launching is the unusual
 * choice here and has to read like one. Removing the control outright would
 * strand every email ora declined wrongly, which is the failure this state
 * makes visible rather than the one it should cause.
 */
export const SECONDARY_LAUNCH_ANYWAY = "Launch anyway";

/** BINDING copy — the secondary action offered whenever a run already exists. */
export const SECONDARY_LAUNCH_ANOTHER = "Launch another";

/**
 * What the host must DO for the primary action. The label is copy; this is the
 * behaviour, so a host switches on the kind and never string-matches the label.
 */
export type ActionKind =
  | "run_analysis"
  | "open_brief"
  | "open_run"
  | "open_decision"
  | "relaunch"
  | "open_needs_you";

const ACTION_KIND: Record<keyof typeof CONTEXT_PRIMARY_ACTION, ActionKind> = {
  not_analyzed: "run_analysis",
  brief_ready: "open_brief",
  // Deliberately the SAME kind as `brief_ready`: the launch form is where a
  // policy gets supplied, so a new ActionKind would buy a second host wiring
  // for a destination that already exists.
  unmatched: "open_brief",
  running: "open_run",
  input_required: "open_decision",
  completed: "relaunch",
  several_runs: "open_needs_you",
};

export interface EmailContextPrimaryAction {
  label: string;
  kind: ActionKind;
  /** The run the action opens, when the action opens a run. */
  runId?: string;
}

export interface EmailContextSecondaryAction {
  label: typeof SECONDARY_LAUNCH_ANOTHER | typeof SECONDARY_LAUNCH_ANYWAY;
}

export interface EmailContext {
  state: EmailContextState;
  pill: string;
  /**
   * The pill's colours when it names a wait, in the shared status-pill tones:
   * amber "Needs You", or white "Pending Email Send" for a run waiting for the
   * reply to be sent — the same words and colour as the Monitor grid
   * (2026-10-05). Null: the strip's own calm grey. `warning`: amber "Completed*" (R53).
   */
  pillTone: "needsYou" | "neutral" | "warning" | null;
  /**
   * The pill's accessible name when its text alone reads wrong (R53,
   * 2026-10-06): "Completed — warnings need acknowledgement" for "Completed*".
   * Null: the text is the name.
   */
  pillAriaLabel?: string | null;
  primaryAction: EmailContextPrimaryAction | null;
  secondaryAction: EmailContextSecondaryAction | null;
}

export interface DeriveEmailContextOptions {
  /**
   * True when there is nothing to resolve — a calendar item, a compose
   * window, or no selection at all. Wins over everything else: the strip must
   * offer NOTHING rather than carry the previous email's action.
   */
  noItem?: boolean;
  /**
   * Format the completed date. Injected only so a host can localise; the
   * default is the shared US formatter. Keeping it a parameter is what keeps
   * this module free of any ambient date policy.
   */
  formatDate?: (iso: string) => string;
}

/**
 * `"<N> runs - <M> needs you"`. The needs-you clause is omitted when M is 0.
 * The separator is a HYPHEN, matching the PRD's table verbatim.
 */
export function severalRunsPill(active: number, needsYou: number): string {
  const runs = `${active} ${active === 1 ? "run" : "runs"}`;
  if (!needsYou || needsYou <= 0) return runs;
  return `${runs} - ${needsYou} needs you`;
}

/**
 * The NEWEST finished run on the email — the row `completed_at` is read from
 * (the substrate orders rows updated_at DESC, instance_id DESC; equal or
 * unreadable times keep that order). Null when no finished row came back.
 */
function newestFinishedRun(
  runs: readonly EmailRunItem[] | null | undefined,
): EmailRunItem | null {
  let newest: EmailRunItem | null = null;
  let newestAt = Number.NEGATIVE_INFINITY;
  for (const run of runs ?? []) {
    if (run.is_active) continue;
    const parsed = run.updated_at ? Date.parse(run.updated_at) : Number.NaN;
    const at = Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
    if (newest === null || at > newestAt) {
      newest = run;
      newestAt = at;
    }
  }
  return newest;
}

/**
 * Whether any finished run on the email has a warning line nobody has
 * acknowledged (R53). Null = unknown: no row carries the boolean (an API or
 * substrate that predates it; .NET then sends null). Fail safe: once the flag
 * is on the wire, a finished row without a confirmed `false` counts as open.
 */
function finishedRunsNeedAcknowledgement(
  runs: readonly EmailRunItem[] | null | undefined,
): boolean | null {
  const rows = runs ?? [];
  if (!rows.some((r) => typeof r.needs_acknowledgement === "boolean")) return null;
  return rows.some((r) => !r.is_active && r.needs_acknowledgement !== false);
}

/**
 * A finished email's pill (R53/R55): the newest finished run's OWN label —
 * never "Completed" for a run that failed, was cancelled, timed out or was
 * rejected — with "*" while any finished run has an open warning line. Amber
 * only for "Completed*" (the shared `warningMarker`, as every other pill).
 */
function finishedPill(
  lookup: EmailRunStateResponse,
  format: (iso: string) => string,
): { pill: string; tone: "warning" | null; ariaLabel: string | null } {
  // No finished row (a fixture, or a page cut short): today's word.
  const status = newestFinishedRun(lookup.runs)?.status || "completed";
  const label = statusDisplayLabel(status);
  const marker = warningMarker({
    status,
    label,
    needsAcknowledgement: finishedRunsNeedAcknowledgement(lookup.runs),
  });
  const shown = marker.asterisk ? `${label}*` : label;
  const rendered = lookup.completed_at ? format(lookup.completed_at) : "";
  // A formatter that cannot parse the value returns its own placeholder;
  // "Completed on —" is not copy anyone authored, so fall back to the bare
  // label. Never "Completed on undefined".
  const pill =
    !rendered || rendered === "—"
      ? shown
      : FINISHED_ON_PILL.replace("{label}", shown).replace("{date}", rendered);
  return { pill, tone: marker.amber ? "warning" : null, ariaLabel: marker.ariaLabel };
}

function isServerState(value: string): value is Exclude<
  EmailContextState,
  "resolving" | "no_item"
> {
  return (SERVER_EMAIL_CONTEXT_STATES as readonly string[]).includes(value);
}

/**
 * Resolve the strip's state, its pill and its actions from ONE lookup response.
 *
 * `lookup === null` means the answer for the CURRENT item is not in hand yet —
 * `resolving`. An action offered before the state is known is an action
 * offered for the wrong email, so `resolving` and `no_item` both offer none.
 */
export function deriveEmailContext(
  lookup: EmailRunStateResponse | null,
  opts: DeriveEmailContextOptions = {},
): EmailContext {
  const format = opts.formatDate ?? formatDate;

  if (opts.noItem) {
    return {
      state: "no_item",
      pill: CONTEXT_PILL.no_item,
      pillTone: null,
      pillAriaLabel: null,
      primaryAction: null,
      secondaryAction: null,
    };
  }

  if (!lookup || !isServerState(lookup.state)) {
    // Unknown server state is treated as still-resolving on purpose: the six
    // literals are closed server-side, so a seventh means the contract moved.
    // Showing nothing is wrong-but-harmless; offering an action derived from a
    // state we do not understand is wrong-and-destructive.
    return {
      state: "resolving",
      pill: CONTEXT_PILL.resolving,
      pillTone: null,
      pillAriaLabel: null,
      primaryAction: null,
      secondaryAction: null,
    };
  }

  const state = lookup.state;
  // `input_required` covers every wait on a person, including a run whose
  // reply is drafted and waiting to be sent. That one reads as the Monitor
  // grid does: white "Pending Email Send", not amber "Needs You".
  const waitingToSend =
    state === "input_required" &&
    (lookup.runs ?? []).some(
      (r) => r.is_active && r.status === "pending_email_send" &&
        (!lookup.primary_run_id || r.instance_id === lookup.primary_run_id),
    );
  // A finished email names its NEWEST finished run by that run's own label and
  // stars it while any finished run has an unacknowledged warning (R53/R55).
  const finished = state === "completed" ? finishedPill(lookup, format) : null;
  const pill =
    state === "several_runs"
      ? severalRunsPill(lookup.active_count, lookup.needs_you_count)
      : finished
        ? finished.pill
        : waitingToSend
          ? STATUS_LABELS.pending_email_send
          : CONTEXT_PILL[state];
  const pillTone: EmailContext["pillTone"] = finished
    ? finished.tone
    : state !== "input_required"
      ? null
      : waitingToSend
        ? "neutral"
        : "needsYou";
  const pillAriaLabel = finished?.ariaLabel ?? null;

  // Story 26741 AC-4: the declined state offers no primary action at all. It
  // is the only server state that does — every other one has a next step the
  // AM is expected to take.
  if (state === "no_action") {
    return {
      state,
      pill,
      pillTone,
      pillAriaLabel,
      primaryAction: null,
      secondaryAction: { label: SECONDARY_LAUNCH_ANYWAY },
    };
  }

  const primaryAction: EmailContextPrimaryAction = {
    label: CONTEXT_PRIMARY_ACTION[state],
    kind: ACTION_KIND[state],
    ...(lookup.primary_run_id ? { runId: lookup.primary_run_id } : {}),
  };

  // Non-null exactly when a run already exists — INCLUDING `completed` and
  // `several_runs`. Load-bearing: the work key admits a second run on another
  // policy or action, and a strip that only ever offered "open the existing
  // run" would block in the interface what the guard was corrected to allow.
  const secondaryAction: EmailContextSecondaryAction | null = lookup.has_any_run
    ? { label: SECONDARY_LAUNCH_ANOTHER }
    : null;

  return { state, pill, pillTone, pillAriaLabel, primaryAction, secondaryAction };
}
